import { Router } from '@angular/router';
import { NotificationSlugEnum } from '@app/generated/graphql';
import { INotificationConfig } from '../interfaces/interface';
import { DEFAULT_MESSAGE_LIFE } from './notification.constants';
import { ORDER_TAB_QUERY_PARAM } from '@app/account/view-order/config/constants';

const DISPLAY_TITLE: string = 'New comment on an order';
const DISPLAY_DESCRIPTION: string = 'A comment was left on an order.';

function resolveOrderNumber(metadata: Record<string, unknown>): string {
  const orderNumber: string | undefined = metadata['order_number'] as
    | string
    | undefined;

  if (orderNumber && orderNumber.trim().length > 0) {
    return orderNumber;
  }

  const orderId: string | undefined = resolveOrderId(metadata);

  return orderId ? `Order #${orderId}` : 'an order';
}

function resolveAuthorName(metadata: Record<string, unknown>): string {
  const authorName: string | undefined = metadata['author_name'] as
    | string
    | undefined;

  return authorName?.trim() || 'Someone';
}

function resolveCommentText(metadata: Record<string, unknown>): string {
  const commentText: string | undefined = metadata['comment_text'] as
    | string
    | undefined;

  return commentText?.trim() || '';
}

function resolveOrderId(metadata: Record<string, unknown>): string | undefined {
  const orderId: number | undefined = metadata['order_id'] as
    | number
    | undefined;

  if (!orderId) {
    return undefined;
  }

  const value: string = String(orderId).trim();
  return value.length > 0 ? value : undefined;
}

export function createOrderCommentNotificationConfig(
  router: Router,
): INotificationConfig {
  return {
    slug: NotificationSlugEnum.OrderComment,
    preference: {
      displayTitle: 'Activity on orders',
      displayDescription: 'Get notified on comments on an order',
      defaultConfig: {},
    },
    message: {
      iconPath: '/assets/icons/general/info.svg',
      displayTitle: DISPLAY_TITLE,
      displayDescription: DISPLAY_DESCRIPTION,
      resolveDisplayTitle: (metadata: Record<string, unknown>): string =>
        `New comment on ${resolveOrderNumber(metadata)}`,
      resolveDisplayDescription: (
        metadata: Record<string, unknown>,
      ): string | undefined => {
        const authorName: string = resolveAuthorName(metadata);
        const commentText: string | undefined = resolveCommentText(metadata);

        if (!commentText) {
          return `${authorName} left a comment.`;
        }

        return `${authorName}: ${commentText}`;
      },
      actionLabel: 'View order',
      action: (metadata: Record<string, unknown>) => {
        const orderId: string | undefined = resolveOrderId(metadata);
        if (!orderId) {
          return;
        }

        router.navigate(['/account/order', orderId], {
          queryParams: { [ORDER_TAB_QUERY_PARAM]: 'comments' },
        });
      },
      life: DEFAULT_MESSAGE_LIFE,
    },
  };
}
